import api from "@/api/v1-jwt/config/api";
import {
  useQuery,
  QueryFunctionContext,
  UseQueryOptions,
} from "@tanstack/react-query";
import sweepQueryKeys from "../query-keys";
import { Sweep, SweepListResponseSchema } from "../types";

type SweepsReportsQueryKey = ReturnType<typeof sweepQueryKeys.reports>;

const fetchSweeps = async ({
  queryKey,
}: QueryFunctionContext<SweepsReportsQueryKey>): Promise<Sweep[]> => {
  const [, userId] = queryKey;
  const url = `/platform/sweeps/${userId}/reports`;
  const { data } = await api.get(url);
  const parsedResponse = SweepListResponseSchema.parse(data);
  return parsedResponse.data;
};

export const sweepsReportsOptions = <TSelected = Sweep[]>(
  userId: string,
  options?: Omit<
    UseQueryOptions<Sweep[], Error, TSelected, SweepsReportsQueryKey>,
    "queryKey" | "queryFn"
  >
): UseQueryOptions<Sweep[], Error, TSelected, SweepsReportsQueryKey> => {
  const defaultOptions: UseQueryOptions<
    Sweep[],
    Error,
    TSelected,
    SweepsReportsQueryKey
  > = {
    queryKey: sweepQueryKeys.reports(userId),
    queryFn: fetchSweeps,
    staleTime: 2 * 60 * 60 * 1000,
  };
  return { ...defaultOptions, ...options };
};

export const useSweepsReports = <TSelected = Sweep[]>(
  userId: string,
  options?: Omit<
    UseQueryOptions<Sweep[], Error, TSelected, SweepsReportsQueryKey>,
    "queryKey" | "queryFn"
  >
) => {
  const queryOptions = sweepsReportsOptions<TSelected>(userId, options);
  return useQuery<Sweep[], Error, TSelected, SweepsReportsQueryKey>({
    ...queryOptions,
    enabled: options?.enabled ?? !!userId,
  });
};
